import { useTranslation } from 'react-i18next';
import {
  HiExclamationCircle,
  HiRefresh,
  HiSave,
  HiTrash
} from 'react-icons/hi';

const getLocale = (language) => (language === 'ru' ? 'ru-RU' : 'en-US');

const formatMoney = (value, language) =>
  new Intl.NumberFormat(getLocale(language), {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(Number(value || 0));

const getTemplateId = (template) =>
  template?.publicTemplateId || template?.templateId || template?._id || template?.id || '';

const getTemplateName = (template) =>
  String(template?.templateName || template?.name || '').trim();

const getTemplateRequest = (template) => {
  if (template?.analyzeRequest && typeof template.analyzeRequest === 'object') return template.analyzeRequest;
  if (template?.template?.analyzeRequest && typeof template.template.analyzeRequest === 'object') return template.template.analyzeRequest;
  return template || {};
};

const getTemplateMeta = (template, language) => {
  const request = getTemplateRequest(template);
  const bits = [];

  if (request?.symbol_a) bits.push(String(request.symbol_a).toUpperCase());
  if (request?.timeframe) bits.push(`${request.timeframe}m`);
  if (request?.total_usdt_one_entry) bits.push(`$${formatMoney(request.total_usdt_one_entry, language)}`);

  return bits.join(' · ');
};

const getTemplateLevelsSummary = (template) => {
  const request = getTemplateRequest(template);
  const positiveLevels = Array.isArray(request?.level_entry_positive) ? request.level_entry_positive : [];
  const negativeLevels = Array.isArray(request?.level_entry_negative) ? request.level_entry_negative : [];

  return {
    positive: positiveLevels.length ? positiveLevels.join(', ') : '',
    negative: negativeLevels.length ? negativeLevels.join(', ') : ''
  };
};

const TemplateRow = ({
  template,
  language,
  activeTemplateId,
  applyingTemplateId,
  onApply,
  onDelete
}) => {
  const { t } = useTranslation();
  const templateId = getTemplateId(template);
  const templateName = getTemplateName(template);
  const templateMeta = getTemplateMeta(template, language);
  const levelsSummary = getTemplateLevelsSummary(template);
  const isActive = templateId && templateId === activeTemplateId;
  const isApplying = applyingTemplateId === templateId;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onApply(template)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onApply(template);
        }
      }}
      className={`group relative rounded-xl border px-3 py-2.5 transition-all ${
        isActive
          ? 'border-blue-500/40 bg-blue-500/10 shadow-lg shadow-blue-950/10'
          : 'border-slate-700/60 bg-slate-900/55 hover:border-slate-500/70 hover:bg-slate-900/70'
      }`}
    >
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          onDelete(template);
        }}
        className="absolute right-3 top-3 rounded-lg border border-slate-700/70 bg-slate-800/80 p-1.5 text-slate-400 transition-all hover:border-rose-500/40 hover:text-rose-300"
        aria-label={t('analysis.templates.delete')}
      >
        <HiTrash className="h-3.5 w-3.5" />
      </button>

      <div className="min-w-0 pr-10">
        <div className="flex items-start justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <h4 className="truncate text-sm font-bold text-white">{templateName || t('analysis.templates.untitled')}</h4>
            {isActive ? (
              <span className="rounded-full border border-blue-500/30 bg-blue-500/10 px-2 py-0.5 text-[9px] font-black uppercase tracking-[0.16em] text-blue-300">
                {t('analysis.templates.active')}
              </span>
            ) : null}
          </div>
          {isApplying ? (
            <span className="mt-0.5 h-3.5 w-3.5 shrink-0 animate-spin rounded-full border-2 border-slate-500 border-t-transparent" />
          ) : null}
        </div>

        {templateMeta ? (
          <div className="mt-1.5 flex items-center justify-between gap-3">
            <p className="min-w-0 truncate text-[11px] text-slate-300">{templateMeta}</p>
            {levelsSummary.positive || levelsSummary.negative ? (
              <div className="shrink-0 rounded-lg border border-sky-500/20 bg-sky-500/8 px-2.5 py-1">
                <div className="flex items-center gap-2 text-[11px] font-semibold tabular-nums">
                  {levelsSummary.positive ? <span className="text-emerald-300">+ {levelsSummary.positive}</span> : null}
                  {levelsSummary.negative ? <span className="text-rose-300">- {levelsSummary.negative}</span> : null}
                </div>
              </div>
            ) : null}
          </div>
        ) : levelsSummary.positive || levelsSummary.negative ? (
          <div className="mt-1.5 flex justify-end">
            <div className="shrink-0 rounded-lg border border-sky-500/20 bg-sky-500/8 px-2.5 py-1">
              <div className="flex items-center gap-2 text-[11px] font-semibold tabular-nums">
                {levelsSummary.positive ? <span className="text-emerald-300">+ {levelsSummary.positive}</span> : null}
                {levelsSummary.negative ? <span className="text-rose-300">- {levelsSummary.negative}</span> : null}
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

const AnalysisTemplatesPanel = ({
  templates = [],
  templateName = '',
  onTemplateNameChange,
  onSave,
  onApply,
  onDelete,
  onRefresh,
  statusMessage,
  isLoading = false,
  isRefreshing = false,
  isSaving = false,
  applyingTemplateId = null,
  activeTemplateId = null,
  canManage = false,
  isUpdatingName = false
}) => {
  const { t, i18n } = useTranslation();
  const shouldShowError = statusMessage?.type === 'error' && statusMessage?.text;

  return (
    <div className="h-full bg-slate-800 p-5 rounded-2xl border border-slate-700/50 shadow-xl text-white flex flex-col">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-lg font-bold tracking-tight">{t('analysis.templates.title')}</h3>
          <p className="mt-1 text-xs leading-relaxed text-slate-400">{t('analysis.templates.desc')}</p>
        </div>

        {canManage ? (
          <button
            type="button"
            onClick={onRefresh}
            disabled={isRefreshing || isLoading}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-700/70 bg-slate-900/60 px-3 py-2 text-[11px] font-black uppercase tracking-[0.14em] text-slate-300 transition-all hover:border-blue-500/40 hover:text-blue-300 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <HiRefresh className={`h-4 w-4 ${isRefreshing || isLoading ? 'animate-spin' : ''}`} />
            {t('analysis.templates.refresh')}
          </button>
        ) : null}
      </div>

      {!canManage ? (
        <div className="mt-4 rounded-2xl border border-dashed border-slate-700 bg-slate-900/40 px-4 py-5 text-sm text-slate-400">
          {t('analysis.templates.auth_required')}
        </div>
      ) : (
        <>
          <div className="mt-4 flex items-center gap-2">
            <input
              type="text"
              value={templateName}
              onChange={(event) => onTemplateNameChange(event.target.value)}
              placeholder={t('analysis.templates.name_placeholder')}
              className="min-w-0 flex-1 rounded-xl border border-slate-600/50 bg-slate-700/50 px-3 py-2 text-xs text-white outline-none transition-all placeholder:text-slate-500 focus:border-blue-500/50 focus:ring-2 focus:ring-blue-500/20"
            />
            <button
              type="button"
              onClick={onSave}
              disabled={isSaving}
              className="inline-flex shrink-0 items-center justify-center gap-1.5 rounded-xl bg-blue-600 px-3 py-2 text-[10px] font-black uppercase tracking-[0.14em] text-white transition-all hover:bg-blue-500 disabled:cursor-not-allowed disabled:bg-slate-700"
            >
              {isSaving ? (
                <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/70 border-t-transparent" />
              ) : (
                <HiSave className="h-3.5 w-3.5" />
              )}
              {isUpdatingName ? t('analysis.templates.update') : t('analysis.templates.save')}
            </button>
          </div>

          {shouldShowError ? (
            <div className="mt-3 flex items-center gap-2 rounded-xl border border-rose-500/20 bg-rose-500/10 px-3 py-2 text-sm text-rose-100">
              <HiExclamationCircle className="h-4 w-4" />
              <span>{statusMessage.text}</span>
            </div>
          ) : null}

          <div className="mt-4 min-h-0 flex-1 overflow-y-auto pr-1">
            <div className="space-y-2">
            {isLoading ? (
              Array.from({ length: 3 }, (_, index) => (
                <div key={index} className="animate-pulse rounded-xl border border-slate-700/60 bg-slate-900/55 p-3">
                  <div className="h-4 w-32 rounded bg-slate-700/70" />
                  <div className="mt-2 h-3 w-48 rounded bg-slate-800/80" />
                  <div className="mt-2 h-3 w-40 rounded bg-slate-800/80" />
                </div>
              ))
            ) : templates.length ? (
              templates.map((template) => (
                <TemplateRow
                  key={getTemplateId(template) || getTemplateName(template)}
                  template={template}
                  language={i18n.language}
                  activeTemplateId={activeTemplateId}
                  applyingTemplateId={applyingTemplateId}
                  onApply={onApply}
                  onDelete={onDelete}
                />
              ))
            ) : (
              <div className="rounded-2xl border border-dashed border-slate-700 bg-slate-900/40 px-4 py-6 text-sm text-slate-400">
                {t('analysis.templates.empty')}
              </div>
            )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AnalysisTemplatesPanel;
